import { syncSportsnewCommandScope } from './_helpers/sportsnewDetailRealtime';
import { sportsnewOddStateIdOf } from './_helpers/sportsnewOddStateId';
import { useSportsnewFixtureRecovery } from './_hooks/useSportsnewFixtureRecovery';
import { useSportsnewListData } from './_hooks/useSportsnewListData';
import { useSportsnewMembershipQuarantine } from './_hooks/useSportsnewMembershipQuarantine';
import { useSportsnewFixtureDetail } from './_hooks/useSportsnewFixtureDetail';
/**
 * SportsnewContext — state + socket management for the /sportsnew/* API.
 *
 * Socket contract (per sport_socket_guide.md):
 *   Namespace : /ws/sports
 *   Emit      : subscribe:sportsnew:list  | unsubscribe:sportsnew:list  | heartbeat:sportsnew:list
 *               subscribe:sportsnew:fixture | unsubscribe:sportsnew:fixture | heartbeat:sportsnew:fixture
 *               subscribe:sportsnew:selected | unsubscribe:sportsnew:selected | heartbeat:sportsnew:selected
 *               ack:sportsnew:patch
 *   Listen    : sportsnew:list:patch | sportsnew:fixture:patch
 *               sportsnew:selected:patch | sportsnew:bet:result
 *               sportsnew:membership:invalidate
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import useServiceContextHook from '../_hooks/service.context.hook';
import { getStoredAuthToken } from '../_helpers/authTokenStorage.helper';
import { useSportsnewSessionOwner, useSportsnewSessionBoundary } from './_hooks/useSportsnewSessionOwner';
import { SportsBetCartContext } from './SportsBetCartContext';
import {
  failCloseSportsnewDetailMarkets,
  failCloseSportsnewFixtureListOdds,
  fxId,
} from './_helpers/sportsnewListPatch';
import { createSportsnewDetailRealtimeLedger } from './_helpers/sportsnewDetailRealtime';
import { isDisplayOnlyPairSlot, marketLifecycleIdentity } from './_helpers/sportsnewDisplayLifecycle';
import { useSportsnewDisplayLifecycle } from './_helpers/useSportsnewDisplayLifecycle';
import { useSportsnewRealtime } from './_helpers/useSportsnewRealtime';
import {
  SPORTSNEW_LIST_STATUS,
  isSportsnewListViewReady,
  sportsnewListRequestKey,
} from './_helpers/sportsnewListRequestState';
import {
  cancelSportsnewListFlight,
  cancelSportsnewListFlights,
} from './_helpers/sportsnewListFlight';
import {
  createSportsnewMembershipRefreshCoordinator,
  scheduleSportsnewInitialSocketMembershipCatchup,
  sportsnewListGenerationDomain,
} from './_helpers/sportsnewMembershipRefreshCoordinator';
import {
  emitSportsnewListHeartbeatWithAckFence,
  emitSportsnewListSubscriptionWithAckFence,
} from './_helpers/sportsnewListSubscriptionAck';
import {
  createSportsnewListSubscriptionRecovery,
} from './_helpers/sportsnewListSubscriptionRecovery';
import {
  isSportsnewFixtureVisibleForMode,
} from './_helpers/sportsnewListFixtureVisibility';
import {
  normalizeSportsnewMembershipInvalidation,
  sportsnewMembershipInvalidationAffectsMode,
} from './_helpers/sportsnewMembershipInvalidation';
import {
  createSportsnewMembershipInvalidationScheduler,
} from './_helpers/sportsnewMembershipInvalidationScheduler';

import {
  SPORTSNEW_WS_DEBUG,
  isListFilterModeScopeReady,
  listSubscriptionChanged,
  sportSlugOf,
} from './_helpers/sportsnewContextConfig';

export const SportsnewContext = createContext();
export { SPORTSNEW_MODES, SPORTSNEW_SPORTS, SPORTSNEW_DISPLAY_SCOPES } from './_helpers/sportsnewContextConfig';
export { fxId, applyListPatch, diffMainOdds, resolveMainOddsUpdatesFromListPatch, applyMainOddsToDetailMarkets, applyOddPatchToFixtureList, applyFixturePatchToFixtureList } from './_helpers/sportsnewListPatch';

export const SportsnewProvider = ({ children }) => {
  const { services, session } = useServiceContextHook();
  const SportsnewService = services?.SportsnewService;
  const { selections, updateOddsInCart, isSelectionInAnyCart } = useContext(SportsBetCartContext);

  /* ── Mode / sport filter ── */
  const [mode, setMode] = useState('prematch'); // prematch | live | all
  const [displayScope, setDisplayScope] = useState('overseas'); // overseas | domestic | live | esports
  const [selectedSport, setSelectedSport] = useState(null); // null = all sports
  const [selectedLeagueId, setSelectedLeagueId] = useState(null);
  const [selectedGameTitleId, setSelectedGameTitleId] = useState(null);
  const [selectedTournamentId, setSelectedTournamentId] = useState(null);

  /* ── Data ── */
  const [categories, setCategories] = useState([]);
  const [leagues, setLeagues] = useState([]);
  // leagueId -> country display name, cross-referenced from the navigation
  // tree (see the effect below) — fixture list rows only carry a flat
  // leagueId/leagueName, never a nested country/flag object, so this is the
  // only reliable source for "which country is this league from" (used to
  // prefix league headers with a flag + country name across all 4 pages).
  const [leagueCountryMap, setLeagueCountryMap] = useState({});
  const [fixtures, setFixtures] = useState([]);
  const fixturesRef = useRef([]);
  const frameDeliveryRef = useRef(null);
  const projectionObserverRef = useRef(null);
  const commitFixtures = useCallback((update) => {
    // Evaluate once: the event snapshot and React commit must share one value.
    const next = typeof update === 'function' ? update(fixturesRef.current) : update;
    fixturesRef.current = next;
    projectionObserverRef.current?.rowsChanged();
    setFixtures(next);
  }, []);
  const [featuredFixtures, setFeaturedFixtures] = useState([]);
  const [membershipInvalidationEpoch, setMembershipInvalidationEpoch] = useState(0);
  const [detailFixture, setDetailFixture] = useState(null);
  const [detailMarkets, setDetailMarkets] = useState([]);
  const [counts, setCounts] = useState(null);
  const [liveCount, setLiveCount] = useState(0);
  const [displaySettings, setDisplaySettings] = useState(null);
  const [comboBonus, setComboBonus] = useState(null);
  const [comboBuckets, setComboBuckets] = useState({});
  const [bettingLimits] = useState(null);
  const [bettingPolicy] = useState(null);

  /* ── Loading / error ── */
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState(null);
  const [listStatus, setListStatus] = useState(SPORTSNEW_LIST_STATUS.IDLE);
  const [resolvedListFilterKey, setResolvedListFilterKey] = useState(null);
  const [listMembershipRevision, setListMembershipRevision] = useState(null);
  const [listMembershipRevisionPending, setListMembershipRevisionPending] = useState(false);

  /* ── Pagination ── */
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [listTotalItems, setListTotalItems] = useState(null);
  const [listSnapshotComplete, setListSnapshotComplete] = useState(false);
  const [pageSize] = useState(30);

  // Reset to page 1 whenever the filter dimensions change so we never show stale pages
  useEffect(() => {
    let current = true;
    // Settle filter resets before the deferred list request, without a nested effect render.
    queueMicrotask(() => { if (current) setPage(1); });
    return () => { current = false; };
  }, [mode, displayScope, selectedSport, selectedLeagueId, selectedGameTitleId, selectedTournamentId]);

  /* ── Socket ── */
  const socketRef = useRef(null);
  const socketGenerationRef = useRef(0);
  const { sessionOwner, sessionOwnerRef } = useSportsnewSessionOwner({
    userId: session?.user?.id, authenticated: Boolean(session?.user),
    authToken: session?.user?.token || getStoredAuthToken() || '',
  });
  const fixtureRecoveryInFlightRef = useRef(new Map());
  const selectedSubscriptionRef = useRef(null);
  const [socketConnected, setSocketConnected] = useState(false);

  /* ── Sequence tracking for patch ack / gap detection ── */
  const listSequenceRef = useRef(null);
  const listPresentationRef = useRef(null);
  const listStreamRef = useRef(null);
  const presentationRecoveryRef = useRef(null);
  const fixtureSequenceRef = useRef(null);
  const selectedSequenceRef = useRef(null);
  const selectedWsFreshnessRef = useRef(new Map());
  const selectedRecoveryInFlightRef = useRef(new Map());

  /* ── Recovery cooldown: prevent calling recovery more than once per 10s ── */
  const listRecoveryInFlightRef = useRef(false);
  const listRecoveryLastRef = useRef(0);

  /* ── filterHash from the last fixture-list API response ── */
  const filterHashRef = useRef(null);
  const listFilterKeyRef = useRef(null);
  const listSubscriptionRef = useRef(null); // { mode, scope, sport, filterHash }
  const listHeartbeatFenceRef = useRef(null);

  /* Per-key single-flight and active-request identity for fixture lists. */
  const fixturesRequestSeqRef = useRef(0);
  const activeListRequestRef = useRef(null);
  const interruptedListRequestRef = useRef(null);
  const listRequestsInFlightRef = useRef(new Map());
  const listLastGoodByKeyRef = useRef(new Map());
  const membershipFailClosedFixturesRef = useRef(new Map());
  const listMembershipRevisionRef = useRef(null);
  const authoritativeListSnapshotRef = useRef(null);
  const fetchFixturesRef = useRef(null);
  const membershipRefreshCoordinatorRef = useRef(null);
  useLayoutEffect(() => {
    const owner = createSportsnewMembershipRefreshCoordinator({
      getRefresh: () => {
        const refresh = fetchFixturesRef.current;
        return typeof refresh === 'function'
          ? () => refresh({ afterCurrentFlight: true, bypassCache: true, automaticRecovery: true })
          : null;
      },
      onPendingChange: setListMembershipRevisionPending,
    });
    membershipRefreshCoordinatorRef.current = owner;
    return () => owner.cancel();
  }, []);

  const scheduleMembershipGenerationRefresh = useCallback(() => {
    // A response that started before this membership event cannot prove the
    // new card/count generation.  Require this sequence or a later one.
    membershipRefreshCoordinatorRef.current.schedule(fixturesRequestSeqRef.current + 1);
  }, []);

  const listSubscriptionRecoveryRef = useRef(null);
  useLayoutEffect(() => {
    const owner = createSportsnewListSubscriptionRecovery({
      attempt: (request, controls) => emitSportsnewListSubscriptionWithAckFence({
        socket: request.socket,
        subscription: request.subscription,
        isCurrent: controls.isCurrent,
        onSettled: controls.onSettled,
      }),
      isRequestCurrent: request => (
        request.socket?.connected === true
        && socketRef.current === request.socket
        && !listSubscriptionChanged(listSubscriptionRef.current, request.subscription)
      ),
      onFirstFailure: (request, { outcome, ack } = {}, cycle = {}) => {
        console.warn('[sportsnew] list room subscription unavailable; bounded rejoin active:', {
          outcome,
          status: ack?.status ?? null,
        });
        return scheduleSportsnewInitialSocketMembershipCatchup({
          activeRequestKey: listFilterKeyRef.current,
          resolvedSnapshot: authoritativeListSnapshotRef.current,
          currentListCacheVersion: null,
          requiredRequestSequence: cycle.requiredRequestSequence,
          missingVersionRequiredRequestSequence: fixturesRequestSeqRef.current + 1,
          generationDomain: sportsnewListGenerationDomain(request.subscription),
          coordinator: membershipRefreshCoordinatorRef.current,
        });
      },
      onSuccess: (request, { ack }, cycle = {}) => {
        if (SPORTSNEW_WS_DEBUG) {
          console.log('[sportsnew] list subscribe generation fence:', { outcome: 'ack', ack });
        }
        return scheduleSportsnewInitialSocketMembershipCatchup({
          activeRequestKey: listFilterKeyRef.current,
          resolvedSnapshot: authoritativeListSnapshotRef.current,
          currentListCacheVersion: ack.currentListCacheVersion,
          requiredRequestSequence: cycle.requiredRequestSequence,
          missingVersionRequiredRequestSequence: fixturesRequestSeqRef.current + 1,
          generationDomain: sportsnewListGenerationDomain(request.subscription),
          coordinator: membershipRefreshCoordinatorRef.current,
        });
      },
    });
    listSubscriptionRecoveryRef.current = owner;
    return () => owner.cancel();
  }, []);

  const subscribeListWithGenerationFence = useCallback((socket, subscription, options = {}) => (
    listSubscriptionRecoveryRef.current.activate({ socket, subscription, options })
  ), []);

  const heartbeatListWithGenerationFence = useCallback((socket, subscription) => {
    listHeartbeatFenceRef.current?.cancel?.();
    listHeartbeatFenceRef.current = emitSportsnewListHeartbeatWithAckFence({
      socket,
      subscription,
      isCurrent: () => (
        socketRef.current === socket
        && !listSubscriptionChanged(listSubscriptionRef.current, subscription)
      ),
      onSettled: result => {
        if (result.outcome !== 'ack' || result.ack?.status !== true) {
          listSubscriptionRecoveryRef.current.markUnhealthy(result, {
            requiredRequestSequence: fixturesRequestSeqRef.current + 1,
          });
        }
      },
    });
  }, []);


  const listFilterKey = useMemo(() => sportsnewListRequestKey({
    mode,
    scope: displayScope,
    sport: sportSlugOf(selectedSport) || 'all',
    league_id: selectedLeagueId,
    game_title_id: selectedGameTitleId,
    tournament_id: selectedTournamentId,
    page,
    limit: pageSize,
  }), [mode, displayScope, selectedSport, selectedLeagueId, selectedGameTitleId, selectedTournamentId, page, pageSize]);
  useLayoutEffect(() => { listFilterKeyRef.current = listFilterKey; }, [listFilterKey]);
  const listReady = isSportsnewListViewReady({
    activeKey: listFilterKey,
    resolvedKey: resolvedListFilterKey,
  });

  // A disconnected pending list owns one recovery intent, never stale data.
  const interruptListRequestForSocket = useCallback(() => {
    const flight = activeListRequestRef.current;
    if (!flight || flight.cancelled
        || listRequestsInFlightRef.current.get(flight.key) !== flight
        || flight.sessionOwner !== sessionOwnerRef.current
        || flight.key !== listFilterKeyRef.current) return false;
    interruptedListRequestRef.current = {
      key: flight.key, sessionOwner: flight.sessionOwner, service: SportsnewService,
    };
    cancelSportsnewListFlight(flight);
    if (listRequestsInFlightRef.current.get(flight.key) === flight) {
      listRequestsInFlightRef.current.delete(flight.key);
    }
    if (activeListRequestRef.current === flight) activeListRequestRef.current = null;
    setLoading(false);
    setListStatus(SPORTSNEW_LIST_STATUS.ERROR);
    setError('연결이 끊겨 경기 목록을 불러오지 못했습니다. 다시 시도해 주세요.');
    return true;
  }, [SportsnewService, sessionOwnerRef]);

  const resumeInterruptedListRequest = useCallback(() => {
    const intent = interruptedListRequestRef.current;
    if (!intent) return;
    interruptedListRequestRef.current = null;
    if (intent.sessionOwner !== sessionOwnerRef.current
        || intent.key !== listFilterKeyRef.current
        || intent.service !== SportsnewService) return;
    const refresh = fetchFixturesRef.current;
    if (typeof refresh === 'function') {
      // The interrupted flight was removed; existing single-flight owns overlap.
      return refresh({ bypassCache: true, automaticRecovery: true });
    }
  }, [SportsnewService, sessionOwnerRef]);

  const cancelListFlights = useCallback(() => {
    interruptedListRequestRef.current = null;
    cancelSportsnewListFlights(listRequestsInFlightRef.current);
    activeListRequestRef.current = null;
  }, []);

  useEffect(() => {
    if (!SportsnewService) return;
    membershipRefreshCoordinatorRef.current.replaceDomain(
      sportsnewListGenerationDomain({
        mode,
        sport: sportSlugOf(selectedSport) || 'all',
      }),
      { reset: true },
    );
    cancelListFlights();
    fixturesRequestSeqRef.current += 1;

    const cachedSnapshot = listLastGoodByKeyRef.current.get(listFilterKey);
    const snapshot = cachedSnapshot?.sessionOwner === sessionOwnerRef.current ? cachedSnapshot : null;
    commitFixtures(failCloseSportsnewFixtureListOdds(
      snapshot?.fixtures ?? [],
      [...membershipFailClosedFixturesRef.current.keys()],
    ));
    setTotalPages(snapshot?.totalPages ?? 1);
    setListTotalItems(snapshot?.totalItems ?? null);
    setListSnapshotComplete(snapshot?.snapshotComplete === true);
    listMembershipRevisionRef.current = snapshot?.membershipRevision ?? null;
    setListMembershipRevision(listMembershipRevisionRef.current);
    setResolvedListFilterKey(snapshot ? listFilterKey : null);
    setError(null);

    const ready = isListFilterModeScopeReady({ mode, scope: displayScope });
    setLoading(ready);
    setListStatus(ready ? SPORTSNEW_LIST_STATUS.LOADING : SPORTSNEW_LIST_STATUS.IDLE);

    const previousSubscription = listSubscriptionRef.current;
    if (previousSubscription && socketRef.current?.connected) {
      socketRef.current.emit('unsubscribe:sportsnew:list', previousSubscription);
    }
    listHeartbeatFenceRef.current?.cancel?.();
    listHeartbeatFenceRef.current = null;
    listSubscriptionRecoveryRef.current.cancel();
    listSubscriptionRef.current = null;
    filterHashRef.current = null;
    listSequenceRef.current = null;
  }, [SportsnewService, sessionOwner, sessionOwnerRef, listFilterKey, mode, displayScope, selectedSport, cancelListFlights, commitFixtures]);

  useEffect(() => () => cancelListFlights(), [cancelListFlights]);

  /* ── Heartbeat interval ref ── */
  const heartbeatRef = useRef(null);

  /* ── Current detail fixture id ── */
  const [detailFixtureId, setDetailFixtureId] = useState(null);
  const detailFixtureIdRef = useRef(null);
  /* Exact click token fences A1 -> B -> A2, including stale RAF callbacks. */
  const latestDetailRequestRef = useRef(null); // Native exact click/mode/scope/RAF token.
  const detailRequestGenerationRef = useRef(0);
  const detailLifecycleGenerationRef = useRef(0);
  const detailRecoveryGenerationRef = useRef(0);
  /* Prematch may paint this cache; LIVE waits for authoritative REST. */
  const detailCacheRef = useRef(new Map());
  const detailRealtimeLedgerRef = useRef(createSportsnewDetailRealtimeLedger());
  useLayoutEffect(() => {
    syncSportsnewCommandScope(detailRealtimeLedgerRef.current, sessionOwnerRef.current, mode, displayScope,
      selections.map(sportsnewOddStateIdOf).filter(id => id != null));
  }, [mode, displayScope, selections, sessionOwner, sessionOwnerRef]);
  /* ── Aborts the in-flight detail request the moment a newer one starts —
     the existing latestDetailRequestRef staleness check already discards a
     late response's DATA, but doesn't stop the browser from downloading it;
     this actually cancels the underlying HTTP request. ── */
  const detailAbortControllerRef = useRef(null);
  /* ── In-progress chunked market render (see renderMarketsProgressively) —
     cancelled whenever a newer fixture is selected before it finishes. ── */
  const marketChunkRafRef = useRef(null);

  /* ── Odds flash for UI animation ── */
  const [oddsFlash, setOddsFlash] = useState({}); // { [oddStateId]: 'up'|'down' }

  const {
    detailMarketsRef,
    displayScopeRef,
    hiddenFixtureIds,
    hiddenMarketIds,
    hiddenOddIds,
    modeRef,
    operatorSuspendedFixtureIds,
    operatorSuspendedFixtureIdsRef,
    pageRef,
    pageSizeRef,
    reconcileRestDetailMarkets,
    selectedGameTitleIdRef,
    selectedLeagueIdRef,
    selectedSportRef,
    selectedTournamentIdRef,
    selectionsRef,
    setOperatorSuspendedFixtureIds,
    updateOperatorSuspendedFixtureIds,
  } = useSportsnewDisplayLifecycle({
    detailFixture,
    detailFixtureId,
    detailFixtureIdRef,
    detailRealtimeLedgerRef,
    detailMarkets,
    displayScope,
    fixtures,
    fixturesRef,
    mode,
    page,
    pageSize,
    selectedGameTitleId,
    selectedLeagueId,
    selectedSport,
    selectedTournamentId,
    selections,
  });

  const { failCloseCartSelectionsForFixtures, quarantineMembershipFixtures, armMembershipQuarantineReopen, failCloseQuarantinedFixtures } = useSportsnewMembershipQuarantine({
    displayScopeRef,
    listFilterKeyRef,
    listMembershipRevisionRef,
    membershipFailClosedFixturesRef,
    modeRef,
    selectionsRef,
    updateOddsInCart,
  });

  const commitDetailMarkets = useCallback((nextOrUpdater) => {
    const nextValue = typeof nextOrUpdater === 'function'
      ? nextOrUpdater(detailMarketsRef.current)
      : nextOrUpdater;
    const candidateMarkets = Array.isArray(nextValue) ? nextValue : [];
    const activeFixtureId = detailFixtureIdRef.current == null
      ? null
      : String(detailFixtureIdRef.current);
    const nextMarkets = activeFixtureId != null
      && membershipFailClosedFixturesRef.current.has(activeFixtureId)
      ? failCloseSportsnewDetailMarkets(candidateMarkets)
      : candidateMarkets;
    detailMarketsRef.current = nextMarkets;
    setDetailMarkets(nextMarkets);
    return nextMarkets;
  }, [detailFixtureIdRef, detailMarketsRef]);

  const membershipInvalidationSchedulerRef = useRef(null);
  useLayoutEffect(() => {
    const owner = createSportsnewMembershipInvalidationScheduler({
      onInvalidate: () => {
        setMembershipInvalidationEpoch((value) => value + 1);
        scheduleMembershipGenerationRefresh();
      },
    });
    membershipInvalidationSchedulerRef.current = owner;
    return () => owner.cancel();
  }, [scheduleMembershipGenerationRefresh]);

  const handleMembershipInvalidation = useCallback((raw) => {
    const invalidation = normalizeSportsnewMembershipInvalidation(raw);
    if (!sportsnewMembershipInvalidationAffectsMode(invalidation, modeRef.current)) return;
    membershipInvalidationSchedulerRef.current.schedule();
  }, [modeRef]);

  const setFixturesFromRealtime = useCallback((updater) => {
    // Between request keys there is deliberately no active list room. Ignore
    // any already-queued patch from the room just left until the new REST
    // snapshot establishes its full subscription identity.
    if (!listSubscriptionRef.current) return;
    commitFixtures(updater);
  }, [commitFixtures]);

  const recoverFixtureDetailSingleFlight = useSportsnewFixtureRecovery({
    frameDeliveryRef,
    SportsnewService, modeRef, displayScopeRef, sessionOwnerRef, socketGenerationRef,
    detailRequestGenerationRef: detailLifecycleGenerationRef, listFilterKeyRef, fixturesRequestSeqRef,
    fixtureRecoveryInFlightRef, detailFixtureIdRef, fixturesRef, selectionsRef,
    socketRef, detailRealtimeLedgerRef, setDetailFixture, reconcileRestDetailMarkets,
    detailMarketsRef, detailAbortControllerRef, latestDetailRequestRef,
    commitDetailMarkets, setDetailLoading, commitFixtures, detailCacheRef,
    updateOddsInCart, selectedFreshnessRef: selectedWsFreshnessRef, membershipFailClosedFixturesRef,
    failCloseCartSelectionsForFixtures,
  });

  const {
    cancelPendingDetailWork,
    clearActiveDetailIfSuspended,
    clearFixtureDetail,
    loadFixtureDetail,
  } = useSportsnewFixtureDetail({
    frameDeliveryRef,
    SportsnewService,
    recoverFixtureDetailSingleFlight,
    sessionOwnerRef,
    fixtureRecoveryInFlightRef,
    selectionsRef,
    detailAbortControllerRef,
    detailCacheRef,
    detailFixtureIdRef,
    detailRealtimeLedgerRef,
    detailRecoveryGenerationRef,
    detailRequestSeqRef: detailRequestGenerationRef,
    detailLifecycleGenerationRef,
    displayScope,
    displayScopeRef,
    fixtureSequenceRef,
    fixturesRef,
    latestDetailRequestRef,
    marketChunkRafRef,
    mode,
    modeRef,
    operatorSuspendedFixtureIdsRef,
    reconcileRestDetailMarkets,
    setDetailFixture,
    setDetailFixtureId,
    setDetailLoading,
    setDetailMarkets: commitDetailMarkets,
  });

  useSportsnewSessionBoundary(sessionOwner, {
    selectedWsFreshnessRef, selectedRecoveryInFlightRef, membershipFailClosedFixturesRef,
    detailCacheRef, listLastGoodByKeyRef, fixtureRecoveryInFlightRef,
    detailRealtimeLedgerRef, modeRef, displayScopeRef, selectionsRef,
    cancelListFlights, clearFixtureDetail, commitFixtures, setResolvedListFilterKey,
  });

  const { acceptListSnapshot, fetchFixtures, requestPresentationListRecovery } = useSportsnewListData({
    listPresentationRef, listStreamRef, presentationRecoveryRef,
    frameDeliveryRef,
    SportsnewService,
    activeListRequestRef,
    interruptedListRequestRef,
    authoritativeListSnapshotRef,
    commitFixtures,
    detailRealtimeLedgerRef,
    displayScope,
    displayScopeRef,
    failCloseQuarantinedFixtures,
    fetchFixturesRef,
    filterHashRef,
    fixturesRef,
    fixturesRequestSeqRef,
    listFilterKeyRef,
    listLastGoodByKeyRef,
    listMembershipRevisionRef,
    listRequestsInFlightRef,
    listSequenceRef,
    listSubscriptionRef,
    membershipFailClosedFixturesRef,
    membershipRefreshCoordinatorRef,
    mode,
    modeRef,
    page,
    pageRef,
    pageSize,
    pageSizeRef,
    recoverFixtureDetailSingleFlight,
    selectedGameTitleId,
    selectedGameTitleIdRef,
    selectedLeagueId,
    selectedLeagueIdRef,
    selectedSport,
    selectedSportRef,
    selectedTournamentId,
    selectedTournamentIdRef,
    sessionOwnerRef,
    setError,
    setListMembershipRevision,
    setListSnapshotComplete,
    setListStatus,
    setListTotalItems,
    setLoading,
    setOperatorSuspendedFixtureIds,
    setResolvedListFilterKey,
    setTotalPages,
    socketGenerationRef,
    socketRef,
    subscribeListWithGenerationFence,
  });

  const { recoverSelectedOddsForCart, projectionObserver, selectedDelivery } = useSportsnewRealtime({
    listPresentationRef, listStreamRef, requestPresentationListRecovery,
    frameDeliveryRef, projectionObserverRef, listReady, socketConnected,
    deliveryDetailGenerationRef: detailLifecycleGenerationRef,
    SportsnewService,
    interruptListRequestForSocket, resumeInterruptedListRequest,
    acceptListSnapshot, armMembershipQuarantineReopen, cancelPendingDetailWork,
    failCloseCartSelectionsForFixtures, failCloseQuarantinedFixtures,
    fixturesRequestSeqRef, listFilterKeyRef, listHeartbeatFenceRef, listSubscriptionRecoveryRef,
    membershipFailClosedFixturesRef, quarantineMembershipFixtures,
    recoverFixtureDetailSingleFlight, scheduleMembershipGenerationRefresh,
    sessionOwnerRef, socketGenerationRef, fixtureRecoveryInFlightRef,
    displayScope,
    clearActiveDetailIfSuspended,
    detailFixtureIdRef,
    detailCacheRef,
    detailMarketsRef,
    detailRealtimeLedgerRef,
    displayScopeRef,
    filterHashRef,
    fixtureSequenceRef,
    fixturesRef,
    isSelectionInAnyCart,
    onMembershipInvalidation: handleMembershipInvalidation,
    listRecoveryInFlightRef,
    listRecoveryLastRef,
    listSequenceRef,
    listSubscriptionRef,
    modeRef,
    pageRef,
    pageSizeRef,
    reconcileRestDetailMarkets,
    selectedGameTitleIdRef,
    selectedLeagueIdRef,
    selectedRecoveryInFlightRef,
    selectedSubscriptionRef,
    selectedSequenceRef,
    selectedTournamentIdRef,
    selectedWsFreshnessRef,
    selectionsRef,
    sessionOwner,
    setDetailFixture,
    setDetailMarkets: commitDetailMarkets,
    setDetailLoading,
    setFixtures: setFixturesFromRealtime,
    setOddsFlash,
    setSocketConnected,
    detailFixtureId, mode, selections, heartbeatRef, heartbeatListWithGenerationFence,
    setCategories, setComboBonus, setComboBuckets, setDisplaySettings,
    socketRef,
    subscribeListWithGenerationFence,
    updateOddsInCart,
    updateOperatorSuspendedFixtureIds,
  });

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const reconcileOnVisible = () => {
      if (document.visibilityState === 'visible') {
        handleMembershipInvalidation({ mode: modeRef.current, reason: 'VISIBILITY_RESUMED' });
      }
    };
    document.addEventListener('visibilitychange', reconcileOnVisible);
    return () => document.removeEventListener('visibilitychange', reconcileOnVisible);
  }, [handleMembershipInvalidation, modeRef]);

  useEffect(() => {
    fetchFixturesRef.current = fetchFixtures;
    return () => {
      if (fetchFixturesRef.current === fetchFixtures) fetchFixturesRef.current = null;
    };
  }, [fetchFixtures]);

  // Deferred, not immediate. `mode` starts at a hardcoded default ('prematch')
  // regardless of which tab is actually loading — each page's own mount
  // effect (e.g. InplayPage sets mode:'live', DomesticPage/InternationalPage
  // reset selectedSport) corrects it a moment later, in the SAME synchronous
  // effects-flush but as a SEPARATE render pass. Firing fetchFixtures()
  // immediately on the FIRST pass fired one REST call for the stale default,
  // immediately followed by a second call once the page's correction landed
  // — visible as both /sportsnew/fixtures?mode=live and ?mode=prematch firing
  // back to back on a single page load. Deferring to a macrotask lets any
  // synchronous corrections from mounting/unmounting page components settle
  // first (each one recreates fetchFixtures and re-schedules this timer,
  // cancelling the previous one), so only the FINAL settled filter state
  // actually reaches the network.
  useEffect(() => {
    const timer = setTimeout(() => { fetchFixtures(); }, 0);
    return () => clearTimeout(timer);
  }, [fetchFixtures, sessionOwner]);

  /* ─────────────────────────────────────────────────────
     API — fetch featured fixtures
  ───────────────────────────────────────────────────── */

  const fetchFeatured = useCallback(async () => {
    if (!SportsnewService) return;
    const res = await SportsnewService.getFeatured({ mode, scope: displayScope }).catch(() => null);
    if (res?.data) {
      setFeaturedFixtures(Array.isArray(res.data) ? res.data : res.data?.fixtures ?? []);
    }
  }, [SportsnewService, mode, displayScope]);

  /* ─────────────────────────────────────────────────────
     API — fetch leagues when sport changes
  ───────────────────────────────────────────────────── */

  useEffect(() => {
    if (!SportsnewService || !selectedSport) {
      let current = true;
      queueMicrotask(() => { if (current) setLeagues([]); });
      return () => { current = false; };
    }
    const slug = typeof selectedSport === 'object' ? selectedSport.slug : selectedSport;
    SportsnewService.getLeagues(slug)
      .then((res) => {
        if (res?.data) setLeagues(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => setLeagues([]));
  }, [SportsnewService, selectedSport]);

  /* ─────────────────────────────────────────────────────
     API — league -> country lookup (navigation tree)
  ───────────────────────────────────────────────────── */

  useEffect(() => {
    if (!SportsnewService) return;
    // No sport param — confirmed via a captured navigation response that
    // omitting it returns EVERY country/league across all sports in one
    // payload (78 countries / 300 leagues), so this only needs to run once
    // per mode rather than being re-fetched on every sport-chip click.
    SportsnewService.getNavigation({ mode: mode === 'all' ? 'prematch' : mode, scope: displayScope })
      .then((res) => {
        const countries = res?.data?.countries;
        if (!Array.isArray(countries)) return;
        const map = {};
        countries.forEach((country) => {
          const countryName = country?.displayName ?? country?.name ?? '';
          (country?.leagues ?? []).forEach((league) => {
            if (league?.leagueId != null) map[league.leagueId] = countryName;
          });
        });
        if (Object.keys(map).length > 0) {
          setLeagueCountryMap((prev) => ({ ...prev, ...map }));
        }
      })
      .catch(() => {});
  }, [SportsnewService, mode, displayScope]);

  /* ─────────────────────────────────────────────────────
     API — fetch counts
  ───────────────────────────────────────────────────── */

  const fetchCounts = useCallback(async () => {
    if (!SportsnewService) return;
    const [countsRes, liveRes] = await Promise.allSettled([
      SportsnewService.getCounts({ mode, scope: displayScope }),
      SportsnewService.getLiveCount({ scope: 'live' }),
    ]);
    if (countsRes.status === 'fulfilled' && countsRes.value?.data) {
      setCounts(countsRes.value.data);
    }
    if (liveRes.status === 'fulfilled') {
      setLiveCount(
        Number(liveRes.value?.data?.count ?? liveRes.value?.data?.total ?? liveRes.value?.count ?? 0)
      );
    }
  }, [SportsnewService, mode, displayScope]);

  useEffect(() => {
    let current = true;
    queueMicrotask(() => { if (current) fetchCounts(); });
    const interval = setInterval(fetchCounts, 60000);
    return () => { current = false; clearInterval(interval); };
  }, [fetchCounts]);

  /* ─────────────────────────────────────────────────────
     API — fixture detail
  ───────────────────────────────────────────────────── */

  /* ─────────────────────────────────────────────────────
     Pagination helpers
  ───────────────────────────────────────────────────── */

  const goToPage = useCallback((nextPage) => {
    setPage(Math.max(1, Math.min(nextPage, totalPages)));
  }, [totalPages]);

  /* ─────────────────────────────────────────────────────
     Unsubscribe list room on unmount
  ───────────────────────────────────────────────────── */

  useEffect(() => () => {
    cancelPendingDetailWork();
    listHeartbeatFenceRef.current?.cancel?.();
    listHeartbeatFenceRef.current = null;
    listSubscriptionRecoveryRef.current.cancel();
    const sub = listSubscriptionRef.current;
    if (socketRef.current?.connected && sub) {
      socketRef.current.emit('unsubscribe:sportsnew:list', sub);
    }
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
  }, [cancelPendingDetailWork]);

  /* ─────────────────────────────────────────────────────
     Visible fixtures — re-apply the league filter client-side.
     The sportsnew:list socket room is keyed by (mode, sport) only — never
     league_id (see docs/sport_socket_guide.md) — so a full-snapshot list
     patch for the room legitimately contains every league in that sport.
     Without this, such a patch silently undoes a league selection made via
     the REST-filtered fetch.
  ───────────────────────────────────────────────────── */

  const visibleFixtures = useMemo(() => {
    if (!listReady) return [];
    let list = fixtures;
    if (selectedLeagueId) {
      list = list.filter((f) => String(f.leagueId ?? f.league?.id ?? f.league_id) === String(selectedLeagueId));
    }
    if (hiddenFixtureIds.size > 0) {
      list = list.filter((f) => !hiddenFixtureIds.has(fxId(f)));
    }
    if (operatorSuspendedFixtureIds.size > 0) {
      list = list.filter((f) => !operatorSuspendedFixtureIds.has(fxId(f)));
    }
    list = list.filter((fixture) => isSportsnewFixtureVisibleForMode(fixture, mode));
    return list;
  }, [fixtures, selectedLeagueId, hiddenFixtureIds, operatorSuspendedFixtureIds, listReady, mode]);

  /* ── Visible detail markets — detailMarkets stays the canonical, never-
     purged "soft delete" source; this is the ready-to-render view with
     evicted markets/odds removed. Pages should render THIS, not
     detailMarkets directly, once they adopt the lifecycle. ── */
  const visibleDetailMarkets = useMemo(() => {
    if (hiddenMarketIds.size === 0 && hiddenOddIds.size === 0) return detailMarkets;
    return detailMarkets
      .filter((m, marketIndex) => !hiddenMarketIds.has(
        marketLifecycleIdentity(m) ?? `idx:${marketIndex}`
      ))
      .map((m) => {
        const key = m.selections ? 'selections' : m.odds ? 'odds' : null;
        if (!key || hiddenOddIds.size === 0) return m;
        const filtered = m[key].filter((o) => isDisplayOnlyPairSlot(o) || !hiddenOddIds.has(o.oddStateId));
        return filtered.length === m[key].length ? m : { ...m, [key]: filtered };
      });
  }, [detailMarkets, hiddenMarketIds, hiddenOddIds]);

  /* ─────────────────────────────────────────────────────
     Context value
  ───────────────────────────────────────────────────── */

  const value = useMemo(() => ({
    /* data */
    categories,
    leagues,
    leagueCountryMap,
    // Soft-delete filtered views — the raw, never-purged canonical arrays
    // stay in internal state (fixtures/detailMarkets refs above); everything
    // outside this provider renders through these instead, so eviction
    // (fixture removed / market or selection hidden after its grace period)
    // is transparent to every page without per-page changes.
    fixtures: visibleFixtures,
    featuredFixtures,
    detailFixture,
    detailFixtureId,
    detailMarkets: visibleDetailMarkets,
    hiddenOddIds,
    hiddenMarketIds,
    hiddenFixtureIds,
    operatorSuspendedFixtureIds,
    counts,
    liveCount,
    displaySettings,
    comboBonus,
    comboBuckets,
    bettingLimits,
    bettingPolicy,

    /* state */
    mode,
    setMode,
    displayScope,
    setDisplayScope,
    selectedSport,
    setSelectedSport,
    selectedLeagueId,
    setSelectedLeagueId,
    selectedGameTitleId,
    setSelectedGameTitleId,
    selectedTournamentId,
    setSelectedTournamentId,
    loading,
    listStatus,
    listReady,
    listMembershipRevision,
    listMembershipRevisionPending,
    membershipInvalidationEpoch,
    detailLoading,
    error,

    /* pagination */
    page,
    totalPages,
    listTotalItems,
    listSnapshotComplete,
    goToPage,

    /* socket */
    socketConnected,
    projectionObserver,
    selectedContinuityPending: selectedDelivery.pending,
    oddsFlash,

    /* actions */
    fetchFixtures,
    fetchFeatured,
    loadFixtureDetail,
    clearFixtureDetail,
    fetchCounts,
    recoverSelectedOddsForCart,
  }), [
    categories, leagues, leagueCountryMap, visibleFixtures, featuredFixtures, detailFixture, detailFixtureId, visibleDetailMarkets,
    hiddenOddIds, hiddenMarketIds, hiddenFixtureIds, operatorSuspendedFixtureIds,
    counts, liveCount, displaySettings, comboBonus, comboBuckets, bettingLimits, bettingPolicy,
    mode, displayScope, selectedSport, selectedLeagueId, selectedGameTitleId, selectedTournamentId, loading, listStatus, listReady, listMembershipRevision, listMembershipRevisionPending, membershipInvalidationEpoch, detailLoading, error,
    page, totalPages, listTotalItems, listSnapshotComplete, goToPage,
    socketConnected, projectionObserver, selectedDelivery.pending, oddsFlash,
    fetchFixtures, fetchFeatured, loadFixtureDetail, clearFixtureDetail, fetchCounts, recoverSelectedOddsForCart,
  ]);

  return (
    <SportsnewContext.Provider value={value}>
      {children}
    </SportsnewContext.Provider>
  );
};

export default SportsnewContext;
